import { Dialog, Flex, Grid, Heading, Text, Tooltip } from '@radix-ui/themes'
import { NavLink } from '@remix-run/react'
import cn from 'clsx'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { AnimatedLogo } from '#app/components/common/logo'
import { Payment } from '#app/components/common/payment'
import {
	Bio,
	Recycling_2,
	ShoppingBag_2,
	Together,
	TransportFree,
} from '#app/components/icon'
import { CloseButton } from '#app/components/ui/close-button/close-button.tsx'
import { type Locale } from '#app/models/locale'
import { Copyright } from '#app/routes/_layout/copyright'
import { I18n } from '#app/routes/_layout/i18n'
import { menu } from '#app/routes/_layout/navbar'
import {
	ROOT_APP_VIEW,
	ROOT_I18NEXT,
	type App,
} from '#app/routes/_layout/route-config.ts'
import { SmartMedia } from '#app/routes/_layout/smart-media'
import { SocialMedia } from '#app/routes/_layout/social-media'

const menuAppend = [
	{
		label: 'ingredients',
		path: '/ingredients',
		Icon: Bio,
	},
	{
		label: 'shipment',
		path: '/shipment',
		Icon: TransportFree,
	},
	{
		label: 'subscription',
		path: '/blog/90e0d643-1208-41a4-92e3-6720bd4db69e',
		Icon: Recycling_2,
	},
	{
		label: 'omnibusOrder',
		path: '/blog/06d63cde-0d93-46a3-bddd-e03988804da8',
		Icon: Together,
	},
	{
		label: 'cart',
		path: '/cart',
		Icon: ShoppingBag_2,
	},
]

/* -------------------------------------------------------------------------------------------------
 * MenuPopover
 * -----------------------------------------------------------------------------------------------*/

type MenuPopoverProps = {
	app: App
	locale: Locale
	setApp: React.Dispatch<React.SetStateAction<App>>
}

const MenuPopover: React.FC<MenuPopoverProps> = ({ locale, setApp }) => {
	const { t } = useTranslation(ROOT_I18NEXT.routeRoot)

	return (
		<Flex direction="column" px="4" className="h-dvh bg-gray-1">
			<Flex
				direction="column"
				position="sticky"
				top="0"
				pt="5"
				pb="2"
				pr="1"
				className="z-10 bg-gray-1"
			>
				<Dialog.Close>
					<CloseButton
						onClose={() =>
							setApp((prev) => ({
								...prev,
								displaySidebar: false,
							}))
						}
					/>
				</Dialog.Close>
			</Flex>
			<Flex direction="column" gapY="4" className="grow">
				<Grid asChild columns="3" gap="1" m="auto">
					<ul className="place-content-center bg-accent-3">
						{[...menu, ...menuAppend].map(({ label, path, Icon }) => (
							<Flex key={label} asChild align="center" justify="center" p="6">
								<li className="group h-28 w-28 bg-gray-1">
									<NavLink
										end={path == ''}
										to={`/${locale}${path}`}
										onClick={(e) => {
											if (label === 'cart') {
												e.preventDefault()
											}
											setApp((prev) => ({
												...prev,
												displaySidebar: label === 'cart',
												sidebarView: ROOT_APP_VIEW.cartView,
											}))
										}}
									>
										{({ isActive }) => (
											<Flex direction="column" align="center" gapX="1">
												<Icon
													width={60}
													height={60}
													className={cn('fill-transparent stroke-12', {
														'stroke-accent-9': isActive,
														'stroke-gray-10': !isActive,
													})}
												/>
												<Text
													size={isActive ? '2' : '1'}
													align="center"
													color={isActive ? 'green' : 'gray'}
													weight={isActive ? 'bold' : 'light'}
												>
													{t(`label.${label}`)}
												</Text>
											</Flex>
										)}
									</NavLink>
								</li>
							</Flex>
						))}
					</ul>
				</Grid>
				<Flex
					direction="column"
					justify="center"
					align="center"
					mt="5"
					gapY="6"
					flexGrow="1"
				>
					<AnimatedLogo width={200} height={200} />
					<SocialMedia />
					<Tooltip
						content={t('label.changeLanguage')}
						side="bottom"
						sideOffset={10}
					>
						<Text>
							<I18n locale={locale} />
						</Text>
					</Tooltip>
					<Flex direction="column" align="center">
						<Heading as="h4" weight="medium" mb="2" size="3">
							{t('label.paymentMethods')}
						</Heading>
						<Payment className="flex flex-wrap items-center justify-center gap-x-2 sm:justify-start" />
					</Flex>
				</Flex>
			</Flex>
			<Flex
				direction="column"
				align="center"
				gapY="3"
				bottom="0"
				mt="5"
				pb="4"
				flexShrink="0"
				className="z-10 bg-gray-1"
			>
				<Copyright />
				<SmartMedia />
			</Flex>
		</Flex>
	)
}
MenuPopover.displayName = 'MenuPopover'

/* -----------------------------------------------------------------------------------------------*/

export { MenuPopover }
